import { Location } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import {
  IonApp,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonLabel,
  IonList,
  IonMenu,
  IonRouterOutlet,
  IonSplitPane,
  IonText,
  IonToolbar,
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService, CommunicationCoreService } from '@ubie/core';
import { addIcons } from 'ionicons';
import {
  add,
  addCircle,
  addOutline,
  alertCircle,
  arrowBackOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkOutline,
  chevronForwardOutline,
  closeCircle,
  closeCircleOutline,
  closeOutline,
  cloudOutline,
  cloudUpload,
  ellipse,
  grid,
  hardwareChipOutline,
  helpCircle,
  home,
  homeOutline,
  keyOutline,
  logOutOutline,
  menu,
  pencilOutline,
  person,
  personAdd,
  personAddOutline,
  personOutline,
  power,
  radioButtonOff,
  searchOutline,
  settings,
  settingsOutline,
  shieldCheckmarkOutline,
  shieldOutline,
  star,
  starOutline,
  timeOutline,
  trashOutline,
  warningOutline,
} from 'ionicons/icons';
import { filter } from 'rxjs/operators';

import { HeaderComponent } from './shared/header/header.component';
@Component({
  imports: [
    IonList,
    IonMenu,
    IonLabel,
    IonIcon,
    IonContent,
    IonSplitPane,
    IonApp,
    RouterModule,
    IonRouterOutlet,
    IonHeader,
    IonToolbar,
    IonImg,
    IonText,
    TranslatePipe,
    HeaderComponent,
  ],
  providers: [TranslateService, Location],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'installer-dashboard';

  readonly showOnlyRouterOutlet = signal(false);
  readonly isMobileScreen = signal(window.innerWidth < 768);

  @HostListener('window:resize')
  onResize(): void {
    this.isMobileScreen.set(window.innerWidth < 768);
  }

  isInstallationsActive(): boolean {
    const path = this.router.url.split('?')[0]; // Remove query parameters
    return (
      path.startsWith('/installations') || path.startsWith('/installation')
    );
  }

  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly communicationCoreService = inject(CommunicationCoreService);

  constructor() {
    addIcons({
      person,
      ellipse,
      home,
      homeOutline,
      keyOutline,
      grid,
      power,
      helpCircle,
      menu,
      closeCircle,
      closeCircleOutline,
      checkmarkOutline,
      checkmarkCircle,
      checkmarkCircleOutline,
      addCircle,
      addOutline,
      personAdd,
      personAddOutline,
      personOutline,
      cloudUpload,
      cloudOutline,
      alertCircle,
      add,
      arrowBackOutline,
      closeOutline,
      chevronForwardOutline,
      radioButtonOff,
      searchOutline,
      settings,
      settingsOutline,
      pencilOutline,
      trashOutline,
      timeOutline,
      shieldOutline,
      shieldCheckmarkOutline,
      star,
      starOutline,
      logOutOutline,
      warningOutline,
      hardwareChipOutline,
    });

    this.authService.getSavedUser();

    this.subscribeNavigationToCheckSideMenu();
    this.translateService.setFallbackLang('publicidb_1');
    this.translateService.use('publicidb_1');

    this.authService.autoLoginEvent.pipe(takeUntilDestroyed()).subscribe({
      next: () => {
        this.translateService.use(
          'admin_' + this.authService.getUserLanguageId(),
        );

        this.initMqttCommunication();
      },
    });
  }

  private subscribeNavigationToCheckSideMenu(): void {
    // Subscribe to router navigation events to update showOnlyRouterOutlet
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        const path = navigationEvent.urlAfterRedirects;
        this.updateShowOnlyRouterOutlet(path);
      });
  }

  private updateShowOnlyRouterOutlet(path: string): void {
    if (
      path.indexOf('/login') > -1 ||
      path.indexOf('/forgot-password') > -1 ||
      path.indexOf('/activation') > -1 ||
      path.indexOf('/registration') > -1 ||
      path.indexOf('/reset-password') > -1
    ) {
      this.showOnlyRouterOutlet.set(true);
    } else {
      this.showOnlyRouterOutlet.set(false);
    }
  }

  private async initMqttCommunication(): Promise<void> {
    // start mqtt communication
    await this.communicationCoreService.connectMqtt();
  }
}
